import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from '../../core/api/smart-review-api.service';
import {
  CurateRequest,
  CurateResponse,
  CurationExportFilters,
  CurationQueueFilters,
  CurationQueueResponse,
  CurationStats,
  CurationStatsFilters,
  ItemIdentity
} from './interfaces/curation.interface';

/**
 * Serviço da Curadoria de Feedback. Consome a API do Smart Review sob
 * `/admin/curation` (Bearer com `smartreview.admin`). Herda auth/headers de SmartReviewApiService.
 */
@Injectable()
export class CurationService extends SmartReviewApiService {
  private get base(): string { return `${this.apiURL}/admin/curation`; }

  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  private buildParams(filters: CurationQueueFilters | CurationExportFilters | CurationStatsFilters): HttpParams {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = (filters as Record<string, string | string[] | undefined>)[key];
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach(item => {
          params = params.append(key, item);
        });
      } else if (value) {
        params = params.set(key, value);
      }
    });
    return params;
  }

  /** GET /queue — lista itens agregados por status (pending/curated/discarded). */
  getQueue(filters: CurationQueueFilters = {}): Observable<CurationQueueResponse> {
    const withDefaults: CurationQueueFilters = { status: 'pending', ...filters };
    return this.http
      .get<CurationQueueResponse>(`${this.base}/queue`, {
        headers: this.jsonHeaders,
        params: this.buildParams(withDefaults)
      })
      .pipe(catchError(this.mainService.handleError));
  }

  /** POST /items/curate — classifica um item; resposta inclui o lever derivado. */
  curateItem(body: CurateRequest): Observable<CurateResponse> {
    return this.http
      .post<CurateResponse>(`${this.base}/items/curate`, body, { headers: this.jsonHeaders })
      .pipe(catchError(this.mainService.handleError));
  }

  /** POST /items/discard — descarta um item; responde com o item descartado (status, curatedAt). */
  discardItem(body: ItemIdentity): Observable<CurateResponse> {
    return this.http
      .post<CurateResponse>(`${this.base}/items/discard`, body, { headers: this.jsonHeaders })
      .pipe(catchError(this.mainService.handleError));
  }

  /** GET /stats — agregados do golden set. */
  getStats(filters: CurationStatsFilters = {}): Observable<CurationStats> {
    return this.http
      .get<CurationStats>(`${this.base}/stats`, {
        headers: this.jsonHeaders,
        params: this.buildParams(filters)
      })
      .pipe(catchError(this.mainService.handleError));
  }

  /**
   * GET /export — JSON (application/json) do golden set: um único array `[...]`.
   * Retorna a resposta completa (texto) para a tela montar o blob/download.
   */
  exportGoldenSet(filters: CurationExportFilters = {}): Observable<HttpResponse<string>> {
    const headers = new HttpHeaders({
      Authorization: this.bearerToken,
      Accept: 'application/json',
      'X-PO-No-Message': 'true'
    });
    return this.http
      .get(`${this.base}/export`, {
        headers,
        params: this.buildParams(filters),
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map(response => response as HttpResponse<string>),
        catchError(this.mainService.handleError)
      );
  }
}
